import { ObservationService } from '../../shared/observation/observation.service';
import { WlxTabViewComponent } from '../../shared/widgets/tabs/tab-view.component';
import { WlxTabComponent } from '../../shared/widgets/tabs/tab.component';
import { Component, OnInit, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { Router, ActivatedRoute, NavigationExtras, UrlSegment, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Subscription } from 'rxjs';
import { Observable } from 'rxjs';
import { NotificationService, UtilService } from '../../shared/index';
import { TranslationService } from '../../shared/translation/translation.service';
import { UserService } from '../../users/shared/user.service';
import { NavigationService } from '../../shared/navigation/navigation.service';
import { MassCommService, Message, CommunicationRecordSearch } from '../shared/index';
import { MassCommMessageEditSendComponent } from './send/mass-comm-message-edit-send.component';
import { MassCommMessageEditMessageComponent } from './message/mass-comm-message-edit-message.component';
import { MassCommParameterService } from '../shared/mass-comm-parameter.service';
import { MassCommParamNav } from '../shared/mass-comm-param-nav.model';
import { IWlComponentCanDeactivateRoute } from '../../shared/route/index';
import { ScheduleModeEnum } from '../shared/schedule-mode.enum';
import { Title } from '@angular/platform-browser';

/**
 * edit a single mass comm message
 */
@Component({
  selector: 'wl-mass-comm-message-edit',
  templateUrl: 'mass-comm-message-edit.component.html',
  styleUrls: [],
  providers: [MassCommParameterService],
    standalone: false
})
export class MassCommMessageEditComponent implements OnInit, OnDestroy, IWlComponentCanDeactivateRoute {
  @ViewChild('sendComponent', { static: false }) private _sendComponent: MassCommMessageEditSendComponent;
  @ViewChild(MassCommMessageEditMessageComponent) private _messageComponent: MassCommMessageEditMessageComponent;
  @ViewChild('tabsContainer') _tabViewComponent: WlxTabViewComponent;
  @ViewChild('massCommProfilesTab') _profilesTab: WlxTabComponent;

  public userAccessLevel: number = 0;
  public isLoading: boolean = false;
  public isSaving: boolean = false;
  public message: Message = null;
  public requestedMessageId: number = 0;
  public isDraft: boolean = false;
  public isResend: boolean = false;
  public isScheduled: boolean = false;

  public shouldCopy: boolean = false;
  public readonly: boolean = false;
  public gettingPreview: boolean = false;
  public deleting: boolean = false;
  public userEmailAddress: string = '';
  public hasNavigateBackRoute: boolean = false;
  public navigateBackText: string = '';
  public navigateBackRoute: string = '';

  public showDeleteModal = false;
  public showUnsavedModal = false;
  public showRecurringModal = false;
  public showInvoiceEmailModal = false;

  private _subscriptions: Array<Subscription> = [];
  private _unchangedMessage: string = '';
  private _navigateAwayDeferred: { allowNavigate(): void; cancelNavigate(): void } = null;
  public _redirectingOnPurpose: boolean = false;
  public tryingToCopyAnEventRelatedMessage: boolean = false;

  /**
   * inject
   * @param _userService
   * @param _router
   */
  public constructor(
    private _userService: UserService,
    private _massCommService: MassCommService,
    private _navigationService: NavigationService,
    private _notificationService: NotificationService,
    private _translationService: TranslationService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _massCommParameterService: MassCommParameterService,
    private _utilService: UtilService,
    private _observationService: ObservationService,
    private _title: Title
  ) {}

  /**
   * on init
   */
  public ngOnInit(): void {
    this.isLoading = true;

    if (this._massCommParameterService.hasNavigatedFrom()) {
      let navFrom = this._massCommParameterService.getNavigatedFromParams();
      this.navigateBackText = navFrom.DisplayText;
      this.navigateBackRoute = navFrom.NavigatedFromRoute;
      this.hasNavigateBackRoute = true;
      this._massCommParameterService.clearNavigatedFromParams();
    } else {
      this.navigateBackText = '';
      this.navigateBackRoute = '';
      this.hasNavigateBackRoute = false;
    }

    // make sure they should be here
    this._userService.getCurrentUserSecurityElementAccessLevel('Send_Mass_Communicatons').then(
      (userAccessLevel: number) => {
        this.userAccessLevel = userAccessLevel;
        if (userAccessLevel < 1) {
          this._router.navigate(['access/denied']);
          return;
        }

        this.isLoading = false;

        //this._subscriptions.push(this._route.params
        //.map(params => params['id'])
        //.subscribe((idStr) => {
        this._subscriptions.push(
          this._route.queryParamMap.subscribe((params) => {
            if (params.get('isInvoiceEmail')) {
              this.showInvoiceEmailModal = true;
            }
          })
        );
        this._subscriptions.push(
          this._route.paramMap.subscribe((params) => {
            let id = parseInt(params.get('id'));
            this.requestedMessageId = id;

            // other flags setup in app routes
            let routeData: any = this._route.snapshot.data;
            this.isDraft = false;
            this.readonly = false;
            this.shouldCopy = false;
            this._redirectingOnPurpose = false;
            if (!routeData) return;

            this.message = null;
            this.isDraft = !!routeData.isDraft;
            this.shouldCopy = !!routeData.shouldCopy;
            this.isResend = !!routeData.isResend;
            this.isScheduled = !!routeData.isScheduled;

            if (userAccessLevel < 2 && (this.isDraft || this.shouldCopy || id <= 0)) {
              // not read only. user has only read access
              this._router.navigate(['access/denied']);
              return;
            }

            let message: Message = new Message();

            message.ScheduleMode = ScheduleModeEnum.Immediate;
            this._userService.getCurrentUser().then((user) => {
              if (!user) return;

              this.userEmailAddress = user.Email;

              // preset for new only
              if (id <= 0) {
                message.SendFrom = user.Email;
                message.SendFromDisplayName = user.FullName;
              }
            });

            if (id <= 0) {
              setTimeout(() => {
                this.message = message;
                this.saveMessageSnapshotForDirtyCompare(4000);
              });
              return;
            }

            this.isLoading = true;

            if (this.isDraft)
              this._massCommService.getDraftMessage(id).then(
                (message) => {
                  this.message = message;
                  this.saveMessageSnapshotForDirtyCompare(4000);
                  this.isLoading = false;
                  this._title.setTitle(this._title.getTitle() + ': ' + message.Subject);

                  if (message && this.shouldCopy) {
                    this.configureInitialSetupForForCopyingMode();
                  }

                  if (message && !this.message.Recurring && this.message.DateAndTimeToSend < new Date()) {
                    // if it's not recurring and scheduled initially to send in the past, default to immediate
                    this.message.DateAndTimeToSend = new Date();
                    this.message.DateAndTimeActuallySent = new Date();
                    this.message.ScheduleMode = ScheduleModeEnum.Immediate;
                  } else if (message && !this.message.Recurring) {
                    this.message.ScheduleMode = ScheduleModeEnum.DelaySend;
                  }
                },
                () => (this.isLoading = false)
              );
            else
              this._massCommService.getMessage(id).then(
                (message) => {
                  this.message = message;

                  this.saveMessageSnapshotForDirtyCompare(4000);
                  this.isLoading = false;
                  this._title.setTitle(this._title.getTitle() + ': ' + message.Subject);

                  if (message && this.shouldCopy) {
                    this.configureInitialSetupForForCopyingMode();
                  }

                  this.readonly = this.message && !this.shouldCopy;
                },
                () => (this.isLoading = false)
              );
          })
        );

        this._subscriptions.push(
          this._observationService.observeMassCommProfilesTabSelected().subscribe((select) => {
            if (select) {
              this._tabViewComponent.select(this._profilesTab);
            }
          })
        );
      },
      () => (this.isLoading = false)
    );
  }

  private configureInitialSetupForForCopyingMode() {
    // when copying, default to immediate mode
    this.message.DateAndTimeToSend = new Date();
    this.message.DateAndTimeActuallySent = new Date();
    this.message.ScheduleMode = ScheduleModeEnum.Immediate;
    this.message.MessageId = 0;
    this.message.Subject += '_COPY';
    this.message.MessageSent = false;

    if (this.message.EventId > 0) {
      this.tryingToCopyAnEventRelatedMessage = true;

      this._translationService
        .getCapitalizeFirstCharacter('communications.mass-comm-message-admin.message-can-not-be-copied-because-its-from-an-event')
        .then((message: string) => {
          this._notificationService.warn(message);
        });
    }
  }

  /**
   * show a preview of the mssage
   */
  public async getMessagePreview(): Promise<void> {
    if (this.gettingPreview) return;

    this.gettingPreview = true;

    let profileId: number = 0;

    try {
      // preview what Save and Send would produce, not raw editor data the debounced blur export has yet to replace
      await this.flushMessageEditor();
      profileId = await this.getFirstRecipientProfileId();
    } catch (e) {
      return;
    } finally {
      this.gettingPreview = false;
    }

    let messageCopy: Message = JSON.parse(JSON.stringify(this.message));

    if (this.isDraft && messageCopy.ProfileSearchParams)
      messageCopy.ReplaceEmailAddressOption = messageCopy.ProfileSearchParams.ReplaceEmailAddressOption;

    this._massCommService.getPreviewMessageUrl(messageCopy, profileId).then(
      (url: string) => {
        this.gettingPreview = false;
        this._navigationService.popupSafeNavigate(url);
      },
      () => (this.gettingPreview = false)
    );
  }

  /**
   * get first profileid from list of recipients
   */
  private getFirstRecipientProfileId(): Promise<number> {
    let profileIdPromise: Promise<number> = Promise.resolve(0);

    if (this.isDraft || this.message.MessageId <= 0) {
      if (this.message.ProfileSearchParams) {
        // profile search
        profileIdPromise = this._massCommService.searchDraftProfiles(this.message.ProfileSearchParams).then((result) => {
          if (result && result.Result && result.Result.length > 0) return result.Result[0].ProfileId;

          return 0;
        });
      }
    } else if (this.message.MessageId > 0) {
      var search = new CommunicationRecordSearch();
      search.MessageId = this.message.MessageId;
      search.PageNumber = 1;
      search.PageSize = 1;

      profileIdPromise = this._massCommService.searchCommunicationRecords(search).then((result) => {
        if (result && result.Result && result.Result.length > 0) return result.Result[0].ProfileId;

        return 0;
      });
    }

    return profileIdPromise;
  }

  /**
   * clean up
   */
  public ngOnDestroy(): void {
    // clean up subscriptions
    if (this._subscriptions) this._subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * save
   */
  private async save(): Promise<void> {
    if (this.userAccessLevel < 2 || this.isSaving || (this.readonly && !this.isResend)) return;

    this.isSaving = true;
    await this.flushMessageEditor();

    let promise: Promise<void> = null;
    let messageClone = this._utilService.clone(this.message) as Message;
    if (this.message.Recurring) {
      messageClone.CronExpression = this._sendComponent._cronBuilder.convertLocalCronExpressionToUtc();
    }

    // always save as draft when saving unless already scheduled or "send" clicked
    if (this.message.MessageId <= 0) {
      promise = this._massCommService.createDraftMessage(messageClone).then((id: number) => {
        this._redirectingOnPurpose = true;
        this._router.navigate(['communications/message/draft', id]);
      });
    } else {
      if (this.isDraft) {
        promise = this._massCommService.updateDraftMessage(messageClone);
      } else {
        this.message.IsResend = this.isResend;
        messageClone.IsResend = this.isResend;
        if (this.isScheduled && this.message.ScheduleMode == ScheduleModeEnum.Immediate) {
          this.message.DateAndTimeToSend = new Date();
          messageClone.DateAndTimeToSend = this.message.DateAndTimeToSend;
        }

        promise = this._massCommService.updateMessage(messageClone);
      }
    }

    if (promise)
      promise.then(
        () => {
          this.isSaving = false;
          this.saveMessageSnapshotForDirtyCompare(1);

          this._translationService.getTitlecase('communications.mass-comm-message-admin.message-saved').then((message: string) => {
            this._notificationService.success(message);
          });
        },
        () => (this.isSaving = false)
      );

    return promise;
  }

  /**
   * writes the editor's inlined-style HTML into message.Message so it, not the raw editor data, is saved or sent
   */
  public flushMessageEditor = (): Promise<void> => this._messageComponent?.flushEditor() ?? Promise.resolve();

  /**
   * clicked save
   */
  public saveClick() {
    if (this.message.ScheduleMode == ScheduleModeEnum.Recurring || this.message.Recurring) {
      this.showRecurringModal = true;
    } else {
      this.save();
    }
  }

  /**
   * copy
   */
  public copyClick() {
    if (this.userAccessLevel < 2 || this.isSaving || !this.message || this.message.MessageId < 0) return;

    if (this.isDraft) this._router.navigate(['communications/message/draft/copy', this.message.MessageId]);
    else this._router.navigate(['communications/message/copy', this.message.MessageId]);
  }

  /**
   * prompt before deleting
   */
  public confirmDeleteMessage(): void {
    if (this.isSaving || this.message.MessageId <= 0) return;

    this.showDeleteModal = true;
  }

  /**
   * delete this message
   */
  public deleteMessgeConfirmed(): void {
    if (this.userAccessLevel < 2 || this.isSaving || this.deleting || this.readonly || this.message.MessageId <= 0) return;

    this.deleting = true;

    this.deleteMessage().then(
      () => {
        this.deleting = false;

        this._translationService.getTitlecase('communications.mass-comm-message-admin.message-deleted').then((message: string) => {
          this._notificationService.success(message);
        });

        this.showDeleteModal = false;

        this._router.navigate(['communications/message-admin']);
      },
      () => (this.deleting = false)
    );
  }

  /**
   * delete
   */
  private deleteMessage(): Promise<void> {
    let promise: Promise<void> = null;

    if (this.isDraft) {
      promise = this._massCommService.deleteDraftMessage(this.message.MessageId);
    } else {
      promise = this._massCommService.deleteMessage(this.message.MessageId);
    }

    return promise;
  }

  /**
   * return false to cancel route navigation.
   * if a promise<boolean> or observable<boolean> is returned, then they'll be awaited.
   *
   * @param routeSnapshot
   * @param stateSnapshot
   */
  canNavigateAway(
    routeSnapshot: ActivatedRouteSnapshot,
    stateSnapshot: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    // can't save, don't need to ask
    if (this.userAccessLevel < 2 || this._redirectingOnPurpose || this.readonly || (this.message && this.message.MessageSent)) return true;

    // oh, it's not even dirty
    if (!this.isDirty()) return true;

    return new Promise<boolean>((resolveFn, rejectFn) => {
      this._navigateAwayDeferred = {
        allowNavigate: () => resolveFn(true),
        cancelNavigate: () => resolveFn(false),
      };

      this.showUnsavedModal = true;
    });
  }

  /**
   * saves the this.message for dirty compare later
   * @param delay
   */
  private saveMessageSnapshotForDirtyCompare(delay: number): void {
    if (delay > 0) setTimeout(() => (this._unchangedMessage = JSON.stringify(this.message)), delay);
    else this._unchangedMessage = JSON.stringify(this.message);
  }

  /**
   * check if the message is changed.
   * Very expensive. Do not use often.
   */
  public isDirty(): boolean {
    if (this.isLoading) return false;

    // compare literals
    let dirty: boolean = JSON.stringify(this.message) != this._unchangedMessage;

    return dirty;
  }

  /**
   * trigger a read of the messages Mesage and Subject for special fields only when send tab is selected to be shown.
   */
  public handleSendTabSelected(): void {
    this._sendComponent.evaluateMessageForSpecialFields();
  }

  public handleNavigateBack(): void {
    if (this._utilService.isNotNullEmptyOrWhitespace(this.navigateBackRoute)) {
      this._navigationService.navigate(this.navigateBackRoute);
    }
  }

  /**
   * when navigating away and the message is unsaved, they clicked "save" on the modal asking if they want to save changes
   */
  public handleUnsavedChangesModalSaveClick(): void {
    this.save()
      .then(() => (this.showUnsavedModal = false))
      .then(() => this._navigateAwayDeferred.allowNavigate());
  }

  /**
   * when navigating away and the message is unsaved, they clicked "cancel" on the modal asking if they want to save changes
   */
  public handleUnsavedChangesModalCancelClick(): void {
    this.showUnsavedModal = false;
    this._navigateAwayDeferred.cancelNavigate();
  }

  /**
   * when navigating away and the message is unsaved, they clicked "no" on the modal asking if they want to save changes
   */
  public handleUnsavedChangesModalNoClick(): void {
    this.showUnsavedModal = false;
    this._navigateAwayDeferred.allowNavigate();
  }

  /**
   * When saving as a draft, click Yes in the confirmation modal to save.
   */
  public handleRecurringModalYesClick(): void {
    this.showRecurringModal = false;
    this.save();
  }

  /**
   * When saving a draft, click Cancel to cancel.
   */
  public handleRecurringModalCancelClick(): void {
    this.showRecurringModal = false;
  }

  /**
   * handles browser back and forward button navigation
   */
  @HostListener('window:beforeunload', ['$event']) public handleWindowBeforeUnload($event: any): boolean {
    if (this.userAccessLevel < 2 || this._redirectingOnPurpose || this.readonly || (this.message && this.message.MessageSent)) return true;

    return !this.isDirty();
  }
}
